import { firebaseApp, firebaseEnabled } from "./auth";
import { WEB_ANALYTICS_SCHEMA_VERSION } from "./analytics-backtest";
import { analyticsPageURL, analyticsReferrerOrigin } from "./analytics-policy";
import { measurementAllowed, subscribeAnalyticsConsent } from "./analytics-consent";
import { createConsentAnalyticsTransport } from "./analytics-transport";
import { safeFunnelParameters } from "./analytics-parameters";
import type { ExperienceMode } from "./experience-mode";

type FunnelEvent =
  | "starter_sample_viewed"
  | "starter_sample_setup_applied"
  | "starter_sample_dismissed"
  | "history_result_opened"
  | "history_rerun_prepared"
  | "app_store_click"
  | "web_to_app_open"
  | "web_to_app_desktop_qr_open"
  | "backtest_started"
  | "backtest_completed"
  | "backtest_failed"
  | "research_workspace_created"
  | "research_run_assigned"
  | "research_comparison_viewed"
  | "research_baseline_set"
  | "research_exported"
  | "research_result_opened"
  | "research_resume_selected"
  | "research_variant_prepared"
  | "research_cloud_sync_started"
  | "research_cloud_sync_completed"
  | "paper_monitoring_viewed"
  | "paper_monitoring_refreshed"
  | "paper_session_started"
  | "strategy_builder_applied"
  | "strategy_builder_validation_failed"
  | "shared_strategy_loaded"
  | "strategy_community_tab_opened"
  | "strategy_community_feed_viewed"
  | "strategy_community_duplicate_started"
  | "strategy_community_recipe_applied"
  | "share_preview_opened"
  | "share_content_selected"
  | "share_link_create_succeeded"
  | "result_shared"
  | "share_cancelled"
  | "share_link_create_failed"
  | "pricing_view"
  | "pricing_catalog_loaded"
  | "pricing_catalog_failed"
  | "ui_experience_selected"
  | "ui_experience_changed";
type FunnelParameters = Record<string, string | number | boolean | undefined>;

const transport = createConsentAnalyticsTransport({
  allowed: measurementAllowed,
  async initialize() {
    if (!firebaseEnabled || !import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) return null;
    const module = await import("firebase/analytics");
    if (!(await module.isSupported()) || !measurementAllowed()) return null;
    // No SDK is loaded before explicit opt-in. Ads remain denied permanently.
    module.setConsent({
      ad_storage: "denied", ad_user_data: "denied",
      ad_personalization: "denied", analytics_storage: "denied",
    });
    const instance = module.initializeAnalytics(firebaseApp(), { config: {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: analyticsPageURL(window.location.href),
      page_referrer: analyticsReferrerOrigin(document.referrer),
    } });
    module.setAnalyticsCollectionEnabled(instance, false);
    return {
      setEnabled(enabled: boolean) {
        if (!enabled) module.setAnalyticsCollectionEnabled(instance, false);
        module.setConsent({
          ad_storage: "denied", ad_user_data: "denied",
          ad_personalization: "denied", analytics_storage: enabled ? "granted" : "denied",
        });
        if (enabled) module.setAnalyticsCollectionEnabled(instance, true);
      },
      event(name: string, parameters: Record<string, string | number | boolean>) {
        module.logEvent(instance, name, parameters);
      },
      properties(parameters: Record<string, string>) {
        module.setUserProperties(instance, parameters);
      },
    };
  },
  started(driver) {
    driver.event("page_view", {
      page_location: analyticsPageURL(window.location.href),
      page_referrer: analyticsReferrerOrigin(document.referrer),
      analytics_schema_version: WEB_ANALYTICS_SCHEMA_VERSION,
    });
  },
});

if (typeof window !== "undefined") {
  subscribeAnalyticsConsent(() => transport.synchronize());
  transport.synchronize();
}

export function trackFunnelEvent(name: FunnelEvent, parameters: FunnelParameters = {}): void {
  if (!measurementAllowed()) return;
  const safeParameters = safeFunnelParameters(parameters);
  const versionedParameters = { ...safeParameters, analytics_schema_version: WEB_ANALYTICS_SCHEMA_VERSION };
  window.dispatchEvent(new CustomEvent("backtest:funnel", { detail: { name, ...versionedParameters } }));
  transport.event(name, {
    ...versionedParameters,
    page_location: analyticsPageURL(window.location.href),
    page_referrer: analyticsReferrerOrigin(document.referrer),
  });
}

/** Sample preview/selection is distinct from a newly executed backtest. */
export function trackStarterSampleEvent(action: "viewed" | "setup_applied" | "dismissed", placement = "backtest"): void {
  trackFunnelEvent(`starter_sample_${action}`, { sample_id: "xau_usd_starter_v1", placement });
}

/** Restoring or preparing a saved run is not a backtest completion. */
export function trackHistoryEvent(action: "result_opened" | "rerun_prepared"): void {
  trackFunnelEvent(`history_${action}`, { source: "saved_history" });
}

export function setAnalyticsExperience(mode: ExperienceMode): void {
  transport.properties({ ui_experience: mode });
}
